import React, { useCallback, useEffect, useState, useMemo } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import PropertyCard from "../components/PropertyCard";
import CityAutocomplete from "../components/CityAutocomplete";
import AdSenseSlot from "../components/AdSenseSlot";
import PropertyMap, { haversineKm, resolvePropertyCoords } from "../components/PropertyMap";
import { ZAMBIAN_CITIES } from "../constants/zambianCities";
import { useTranslation } from "react-i18next";
import { List, Map as MapIcon, MousePointerClick, X, Navigation } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SEARCH_SLOT = process.env.REACT_APP_ADSENSE_SLOT_SEARCH || "";
const AD_EVERY = 6;

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export default function SearchPage() {
  const { t } = useTranslation();
  const query = useQuery();
  const initCity = query.get("city") || "";
  const initType = query.get("type") || "";
  const [city, setCity] = useState(initCity);
  const [type, setType] = useState(initType);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list");
  // Draw-radius filter state — copper circle on the Leaflet map + Haversine
  // filter applied to the results list. Center is set by clicking the map.
  const [drawMode, setDrawMode] = useState(false);
  const [radiusCenter, setRadiusCenter] = useState(null); // [lat, lng]
  const [radiusKm, setRadiusKm] = useState(5);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (city) params.city = city;
    if (type) params.type = type;
    axios.get(`${API}/properties`, { params })
      .then(({ data }) => setItems(data))
      .finally(() => setLoading(false));
  }, [city, type]);

  useEffect(() => { load(); }, [type, load]);

  const submit = (e) => { e.preventDefault(); load(); };

  // Apply the drawn-radius filter. When no center is set, every listing
  // passes through — same behaviour as before the feature landed.
  const filtered = useMemo(() => {
    if (!radiusCenter) return items;
    return items.filter((p) => haversineKm(resolvePropertyCoords(p), radiusCenter) <= (Number(radiusKm) || 5));
  }, [items, radiusCenter, radiusKm]);

  const setCenter = useCallback((coords) => {
    setRadiusCenter(coords);
    setDrawMode(false);
  }, []);

  // Preset chip handlers — copper "Center on my location" (browser geolocation
  // via the standard permission prompt) and 3 major-city chips that jump the
  // circle to the city's centroid instantly.
  const centerOnCity = useCallback((name) => {
    const c = ZAMBIAN_CITIES.find((x) => x.name === name);
    if (!c) return;
    setCenter([c.lat, c.lng]);
  }, [setCenter]);

  const centerOnMyLocation = useCallback(() => {
    if (!navigator.geolocation) return toast.error("Your browser blocks geolocation");
    navigator.geolocation.getCurrentPosition(
      (pos) => setCenter([pos.coords.latitude, pos.coords.longitude]),
      () => toast.error("Location permission denied — pick a city or draw the radius instead"),
      { enableHighAccuracy: false, timeout: 6000, maximumAge: 60000 },
    );
  }, [setCenter]);

  const clearRadius = () => { setRadiusCenter(null); setDrawMode(false); };

  const renderResults = () => {
    const rows = [];
    for (let i = 0; i < filtered.length; i += AD_EVERY) {
      const chunk = filtered.slice(i, i + AD_EVERY);
      rows.push(
        <div key={`row-${i}`} className="grid grid-cols-1 gap-6 md:grid-cols-12">
          {chunk.map((p) => <PropertyCard key={p.id} property={p} />)}
        </div>
      );
      const hasMore = i + AD_EVERY < filtered.length;
      if (SEARCH_SLOT && hasMore) {
        rows.push(
          <div key={`ad-${i}`} data-testid={`adsense-search-row-${i}`}>
            <div className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-slate-400">{t("search.sponsored")}</div>
            <AdSenseSlot
              slot={SEARCH_SLOT}
              testId={`adsense-search-slot-${i}`}
              className="overflow-hidden rounded-3xl bg-white p-4 card-shadow"
            />
          </div>
        );
      }
    }
    return <div className="space-y-8">{rows}</div>;
  };

  return (
    <div className="mx-auto max-w-7xl px-6 py-10 lg:px-8">
      <div className="mb-8">
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">{t("search.title")}</h1>
        <p className="mt-2 text-slate-600">{t("search.subtitle")}</p>
      </div>

      <form onSubmit={submit} className="mb-8 flex flex-col gap-3 rounded-3xl border border-slate-200 bg-white p-4 sm:flex-row sm:items-center">
        <div className="flex flex-1 items-center gap-2 rounded-full border border-slate-200 px-4">
          <CityAutocomplete
            value={city}
            onChange={setCity}
            onSelect={() => load()}
            onSubmit={submit}
            placeholder={t("search.cityPlaceholder")}
            className="flex-1"
            inputTestId="search-city-input"
          />
        </div>
        <select
          data-testid="search-type-select"
          value={type} onChange={(e) => setType(e.target.value)}
          className="rounded-full border border-slate-200 px-4 py-3 text-sm outline-none focus:border-copper"
        >
          <option value="">{t("search.typeAny")}</option>
          <option value="hotel">{t("search.typeHotel")}</option>
          <option value="lodge">{t("search.typeLodge")}</option>
          <option value="guest_house">{t("search.typeGuestHouse")}</option>
          <option value="apartment">{t("search.typeApartment")}</option>
        </select>
        <button data-testid="search-submit-btn" type="submit" className="btn-primary text-sm">{t("search.submit")}</button>
      </form>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="text-xs uppercase tracking-widest text-slate-500">
          {loading ? t("search.loading") : t("search.stayCount", { count: filtered.length })}
          {radiusCenter && !loading && (
            <span className="ml-2 rounded-full bg-copper/10 px-2 py-0.5 text-[10px] font-semibold text-copper">
              within {radiusKm} km · {items.length - filtered.length} filtered out
            </span>
          )}
        </div>
        <div className="inline-flex rounded-full bg-slate-100 p-1" data-testid="search-view-toggle">
          <button
            type="button"
            onClick={() => setView("list")}
            data-testid="view-list-btn"
            className={`flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-semibold transition ${
              view === "list" ? "bg-white text-copper shadow" : "text-slate-500 hover:text-slate-700"
            }`}
          >
            <List className="h-3.5 w-3.5" /> {t("search.toggleList")}
          </button>
          <button
            type="button"
            onClick={() => setView("map")}
            data-testid="view-map-btn"
            className={`flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-semibold transition ${
              view === "map" ? "bg-white text-copper shadow" : "text-slate-500 hover:text-slate-700"
            }`}
          >
            <MapIcon className="h-3.5 w-3.5" /> {t("search.toggleMap")}
          </button>
        </div>
      </div>

      {view === "map" && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-2xl border border-slate-200 bg-white p-3 card-shadow" data-testid="radius-controls">
          <button
            type="button"
            onClick={() => setDrawMode((v) => !v)}
            data-testid="draw-radius-btn"
            className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition ${
              drawMode ? "bg-copper text-white" : "border border-copper/40 text-copper hover:bg-copper/5"
            }`}
          >
            <MousePointerClick className="h-3.5 w-3.5" />
            {drawMode ? "Click the map to place the center…" : radiusCenter ? "Move radius" : "Draw radius filter"}
          </button>
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-400">
            <span className="hidden sm:inline">or</span>
          </div>
          <button
            type="button"
            onClick={centerOnMyLocation}
            data-testid="radius-preset-me"
            className="flex items-center gap-1 rounded-full border border-forest/30 px-3 py-1.5 text-xs font-semibold text-forest hover:bg-forest/5"
          >
            <Navigation className="h-3 w-3" /> My location
          </button>
          {["Lusaka", "Livingstone", "Ndola"].map((name) => (
            <button
              key={name}
              type="button"
              onClick={() => centerOnCity(name)}
              data-testid={`radius-preset-${name.toLowerCase()}`}
              className="rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-copper hover:text-copper"
            >
              {name}
            </button>
          ))}
          {radiusCenter && (
            <>
              <label className="ml-auto flex items-center gap-2 text-xs text-slate-600">
                <span className="uppercase tracking-widest text-slate-500">Radius</span>
                <input
                  type="range"
                  min={1} max={50} step={1}
                  value={radiusKm}
                  onChange={(e) => setRadiusKm(Number(e.target.value))}
                  data-testid="radius-slider"
                  className="w-40 accent-copper"
                />
                <span className="w-14 text-right font-semibold text-slate-800" data-testid="radius-value">{radiusKm} km</span>
              </label>
              <button
                type="button"
                onClick={clearRadius}
                data-testid="clear-radius-btn"
                className="flex items-center gap-1 text-xs text-rose-600 hover:text-rose-800"
              >
                <X className="h-3 w-3" /> Clear
              </button>
            </>
          )}
        </div>
      )}

      {loading ? (
        <div className="rounded-3xl bg-white p-10 text-center text-slate-500">{t("search.loading")}</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-3xl bg-white p-10 text-center text-slate-500" data-testid="search-empty-state">
          {radiusCenter ? `No stays inside the ${radiusKm} km radius. Widen the slider or clear the filter.` : t("search.noResults")}
        </div>
      ) : view === "map" ? (
        <PropertyMap
          properties={filtered}
          height={600}
          drawMode={drawMode}
          radiusCenter={radiusCenter}
          radiusKm={radiusKm}
          onCenterChange={setCenter}
        />
      ) : renderResults()}
    </div>
  );
}
